import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/lib/api/api-client'

/**
 * One dispersion channel this tenant can set an account to pay by.
 *
 * PascalCase because the core's webapp lane serializes with Newtonsoft and a DefaultContractResolver:
 * every property arrives exactly as C# named it. Typed camelCase the whole record reads as undefined and
 * the select renders rows with no text — which is what the provider-selectable list actually did in QA
 * before somebody noticed.
 */
export interface PaymentRuleOption {
  /**
   * What the PUT takes back. The id of THIS environment and nothing to hardcode against: the catalogue
   * numbers its rows differently in QA and production, so the value that came down is the value that
   * goes back up.
   */
  Id: number
  /** The human label — «Kuspit Integration». */
  PaymentType: string
  /** The channel the send job would use, shown as a badge next to the label. */
  ChannelCode: string
  /**
   * Whether the tenant has that integration CONNECTED, and not merely available to it. False does not
   * hide the option — being offered already means the service is theirs — it only earns a note, so
   * nobody discovers it at the first payment instead.
   */
  Connected: boolean
}

/**
 * The channels this tenant may choose from.
 *
 * An EMPTY list is a meaningful answer and not an error: it means either that no integration of this
 * tenant disperses, or that the core could not ask th_services. Both lead to the same screen — the
 * account keeps the only option that is always true, which is no payment integration at all.
 *
 * `isError` is therefore NOT the interesting flag here and the caller barely uses it: the core answers
 * 200 with an empty list when the question could not be answered, precisely so a screen never has to
 * decide what an outage means.
 */
export function usePaymentRules() {
  return useQuery<PaymentRuleOption[]>({
    queryKey: ['payment-rules'],
    queryFn: ({ signal }) => apiClient.get<PaymentRuleOption[]>('/api/payment-rules', signal),
    staleTime: 5 * 60 * 1000,
  })
}

export interface SetAccountPaymentRuleInput {
  accountId: number
  /** Null clears it, which leaves the account unable to order payments — a legitimate state. */
  paymentRuleId: number | null
}

/**
 * Sets, changes or clears which channel one account disperses through.
 *
 * Its own call and not part of saving the account: it is the only field that decides whether money can
 * leave, the core gave it its own route for that reason, and folding it into the form's PUT would make
 * it something whoever renames an account also saves.
 */
export function useSetAccountPaymentRule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ accountId, paymentRuleId }: SetAccountPaymentRuleInput) =>
      apiClient.put<void>(`/api/accounts/${accountId}/payment-rule`, { paymentRuleId }),
    onSuccess: () => {
      // Both lists carry the verdict this just changed: the grid paints its badge from it and the
      // payment picker decides with it. Leaving either stale shows somebody the account they just fixed,
      // still blocked.
      void queryClient.invalidateQueries({ queryKey: ['accounts'] })
      void queryClient.invalidateQueries({ queryKey: ['accounts', 'payment-ordering'] })
    },
  })
}
